import { useTranslation } from "react-i18next";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebounce } from "@uidotdev/usehooks";
import styled from "styled-components";

import {
  Select,
  SelectItem,
  SelectLabel,
  SelectProvider,
} from "../form/select";

import { Color } from "../../constants/color";
import { BreakpointsQuery } from "../../constants/device";
import * as Ariakit from "@ariakit/react";
import {
  CalendarDays,
  ChevronDown,
  GraduationCap,
  Search,
  TriangleAlert,
  Users,
} from "lucide-react";
import { getCourseIcon } from "../../utils/utils";
import { avvisoUnico } from "../../features/notifiche/avvisi";
import {
  useCertificatesDataset,
  usePrenotabili,
} from "../../features/certificate/useCertificatesDataset";
import {
  filtraPerSedeECorso,
  filtraPerStato,
  filtraPerTesto,
  gruppiPerCorso,
  nomeCorso,
  opzioniSede,
  statoDaCodice,
} from "../../features/certificate/pipeline";
import {
  chiaveRiga,
  partecipanti,
  selezionabile,
  selezionata,
  senzaRiga,
  toggleSelezione,
} from "../../features/certificate/selezione";
import { assignMissingCourse } from "../../features/course/utils";
import {
  BadgeStatoAttestato,
  InfoPrenotazione,
  ScaricaAttestato,
} from "./riga-attestato";
import {
  BookableEmployeeType,
  DocumentData,
  RigaPrenotabile,
  RigaSelezionabile,
  SelectedBookableEmployeeType,
  SelezionePrenotazione,
} from "../../types";
import { useAppDispatch } from "../../app/hooks";
import { vaiAllaPrenotazione } from "../../features/employee/prenotazione";
import { BarLoader } from "react-spinners";
import { BookableEmployees } from "./BookableEmployees";
import { StatoVuoto } from "./stato-vuoto";
import Suggerimento from "../tooltip/suggerimento";

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2rem 4rem;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: end;
  gap: 2rem;
  @media ${BreakpointsQuery.tablet} {
    justify-content: center;
    gap: 1rem;
  }
`;

const SearchInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  & label {
    margin-left: 0.5rem;
    font-family: Avenir Next, sans-serif;
    font-weight: bold;
    color: ${Color.LIGHT_BLUE};
  }
  & input {
    border: 1px solid ${Color.LIGHT_BLUE};
    border-radius: 1rem;
    padding: 0.2rem 1rem;
    width: 100%;
  }
`;

const SearchButton = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Color.LIGHT_BLUE};
  color: ${Color.WHITE};
  top: 0;
  right: 0;
  bottom: 0;
  width: 2.5rem;
  border-radius: 0 1rem 1rem 0;
`;

type FiltersType = {
  corso: string;
  sede: string;
  stato: number;
};

// Voci della select STATO, con i codici di `statoDaCodice` nella pipeline.
// 0 = nessun filtro. Il parametro `?status=N` dell'indirizzo appartiene alla
// tab Scadenza (ci arrivano i KPI): questa tab parte sempre da TUTTI, anche
// dopo un refresh su un indirizzo che lo porta. Legge invece `?corso=`.
// "In scadenza" vale sei mesi (`CertificateStatusRule` del backend): qui non
// c'e' il selettore 1/2/6 mesi della tab Scadenza, quindi lo dice la voce.
const statoAttestati: { [key: number]: string } = {
  0: "TUTTI",
  1: "VALIDO",
  2: "IN SCADENZA (ENTRO 6 MESI)",
  3: "SCADUTO",
  4: "MANCANTI",
};

export default function RicercaPerCorso() {
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.search);
  const filterTitoloCorso = urlParams.get("corso") || "";

  // Pipeline condivisa: lista nell'ordine canonico del backend con lo stato
  // già calcolato, sedi di lavoro e catalogo, recuperati una volta per visita
  // alla rotta e condivisi con le altre tab.
  const {
    attestati: tuttiAttestati,
    sediDiLavoro,
    catalogo,
    loading,
  } = useCertificatesDataset();

  const sedeAttestati = useMemo(
    () => opzioniSede(sediDiLavoro, t("attestati-sede-tutte")),
    [sediDiLavoro, t]
  );

  const [filters, setFilters] = useState<FiltersType>({
    corso: filterTitoloCorso,
    sede: "",
    stato: 0,
  });

  const debouncedFilters = useDebounce(filters, 300);

  // Filtri in pagina sul set condiviso (sede e corso in AND, poi stato) e
  // raggruppamento per corso: gli accordion sono in ordine alfabetico di
  // titolo, le righe dentro ogni accordion restano nell'ordine del backend.
  // Nessun riordinamento dei record, nessuna richiesta al cambio di filtro.
  const gruppi = useMemo(() => {
    const corsi = filtraPerStato(
      filtraPerSedeECorso(tuttiAttestati, debouncedFilters, catalogo),
      statoDaCodice(debouncedFilters.stato)
    );
    return gruppiPerCorso(corsi, catalogo);
  }, [tuttiAttestati, catalogo, debouncedFilters]);

  // Un risultato vuoto si spiega in due modi diversi: o i filtri escludono
  // tutto, o non c'e' ancora nulla da mostrare.
  const filtriAttivi =
    !!debouncedFilters.corso ||
    !!debouncedFilters.sede ||
    debouncedFilters.stato !== 0;

  const selectStato = Ariakit.useSelectStore({
    value: `${filters.stato}`,
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, stato: +value }));
    },
  });

  const selectSede = Ariakit.useSelectStore({
    value: filters.sede,
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, sede: value }));
    },
  });

  return (
    <Layout>
      <SearchContainer>
        <SearchInput>
          <label htmlFor="searchCorso">CORSO</label>
          <div
            style={{
              position: "relative",
            }}
          >
            <input
              name="searchCorso"
              type="text"
              placeholder="Ricerca per corso"
              aria-label="Ricerca per corso"
              value={filters.corso}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, corso: e.target.value }))
              }
            />
            <SearchButton>
              <Search color={Color.WHITE} size={16} width={16} />
            </SearchButton>
          </div>
        </SearchInput>
        <div>
          <SelectProvider store={selectSede}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.1rem",
              }}
            >
              <SelectLabel
                style={{
                  paddingLeft: "0.5rem",
                }}
              >
                SEDE AZIENDALE
              </SelectLabel>
              <Select
                style={{
                  minWidth: "200px",
                }}
                renderValue={(value) => sedeAttestati[`${value}`]}
              >
                {Object.entries(sedeAttestati).map(([key, value]) => (
                  <SelectItem key={`sedeAttestato_${key}`} value={key}>
                    {value}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </SelectProvider>
        </div>
        <div>
          <SelectProvider store={selectStato}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.1rem",
              }}
            >
              <SelectLabel
                style={{
                  paddingLeft: "0.5rem",
                }}
              >
                STATO
              </SelectLabel>
              <Select
                style={{
                  minWidth: "200px",
                }}
                renderValue={(value) => statoAttestati[+value]}
              >
                {Object.entries(statoAttestati).map(([key, value]) => (
                  <SelectItem key={`statoAttestato_${key}`} value={key}>
                    {value}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </SelectProvider>
        </div>
      </SearchContainer>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2rem",
          width: "78rem",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              flexDirection: "column",
              fontSize: "1.5rem",
              marginTop: "4rem",
            }}
          >
            <p>Caricamento corsi...</p>
            <BarLoader width="20ch" loading={loading} />
          </div>
        ) : (
          <>
            {gruppi.length === 0 && (
              <StatoVuoto
                icona={GraduationCap}
                titolo={
                  filtriAttivi
                    ? t("attestati-tab-corso-vuoto-filtri")
                    : t("attestati-tab-corso-vuoto")
                }
                suggerimento={
                  filtriAttivi
                    ? t("attestati-tab-corso-vuoto-filtri-suggerimento")
                    : t("attestati-tab-corso-vuoto-suggerimento")
                }
              />
            )}
            {gruppi.map((gruppo) => (
              <div
                key={gruppo.chiave}
                style={{
                  width: "100%",
                  filter: "drop-shadow(4px -4px 5px var(--shadow800))",
                }}
              >
                <CourseAccordion
                  icon={getCourseIcon(gruppo.attestati[0].metadata.tipo_corso)}
                  title={gruppo.titolo}
                  code={gruppo.codice}
                  attestati={gruppo.attestati}
                  catalogo={catalogo}
                  isOpen={
                    filterTitoloCorso === gruppo.titolo ? true : undefined
                  }
                />
              </div>
            ))}
          </>
        )}
      </div>
    </Layout>
  );
}

const Disclosure = styled(Ariakit.Disclosure)`
  border-radius: 0.8rem;
  background-color: ${Color.WHITE};
  width: 100%;
  position: relative;
  overflow: hidden;
  border: none;
  padding: 0.5rem 1.5rem;

  &:before {
    position: absolute;
    content: "";
    top: 0;
    left: 0;
    bottom: 0;
    width: 1rem;
    background-color: ${Color.LIGHT_BLUE};
  }

  & p[role='button'] {
    position: absolute;
    right: 0;
    top: 12px;
    bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 2rem;
    border-radius: 0.8rem 0 0 0.8rem;
    background-color: ${Color.GREEN};
    color: ${Color.WHITE};
    font-weight: bold;
`;

const CourseTitle = styled.p`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1 1 auto;
  margin: 0;
`;

const DisclosureContent = styled(Ariakit.DisclosureContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  width: calc(100% - 0.8rem);
  margin: -8px auto 0 auto;

  background-color: ${Color.CERAMIC};
  border-bottom-left-radius: 1.2rem;
  border-bottom-right-radius: 1.2rem;
  padding: 1.6rem 2rem;
  ::-webkit-scrollbar {
    position: relative;
    display: flex;
    left: 4rem;
    width: 0.6rem;
    border-radius: 4rem;
  }
  ::-webkit-scrollbar-button {
    display: none;
  }
  ::-webkit-scrollbar-track {
    border-radius: 4rem;
    background-color: ${Color.LIGHT_BLUE};
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4rem;
    background-color: ${Color.LIGHT_BLUE};
  }
  ::-webkit-scrollbar-track-piece {
    border-radius: 4rem;
    background-color: ${Color.GREY};
  }
`;

const TableAttestati = styled.div`
  display: grid;
  grid-template-columns: auto 150px 200px 150px 150px 100px 60px;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: bold;
`;

const TableAttestatiHeader = styled(TableAttestati)`
  text-transform: uppercase;
  color: ${Color.LIGHT_BLUE};
`;

const TableAttestatiRow = styled(TableAttestati)`
  border: 1px solid ${Color.GREY};
  border-radius: 16px;
  text-transform: uppercase;
  color: ${Color.DARK_GREY};
  background-color: ${Color.LIGHT_GREY};
  min-height: 3.2rem;
`;

const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${Color.WHITE};
  background-color: ${Color.BLUE};
  border: 0;
  padding: 0.3em 1.5em;
  border-radius: 8px;
  font-size: 1.6em;
  font-weight: bold;
  cursor: pointer;
`;

function CourseAccordion({
  icon,
  title,
  code,
  attestati,
  catalogo,
  isOpen,
}: {
  icon?: React.ReactNode;
  title: string;
  code: string;
  attestati: DocumentData[];
  catalogo: Map<string, string>;
  isOpen?: boolean;
}) {
  const dispatch = useAppDispatch();

  // Le tendine PRENOTA arrivano dal batch condiviso, corrette in sessione;
  // finché non ci sono il dettaglio mostra il caricamento, come prima con la
  // chiamata per corso. Un corso senza codice (vecchi import) non ha voci.
  const { voci, segnaAssegnato, segnaAnnullato } = usePrenotabili();
  const loading = voci === null;
  const bookableEmployees = useMemo<BookableEmployeeType[]>(
    () => (code && voci ? voci[code] ?? [] : []),
    [voci, code]
  );
  const [selectedEmployee, setSelectedEmployee] = useState<
    SelectedBookableEmployeeType[]
  >([]);
  // Corsi mancanti (id del record) la cui assegnazione è stata annullata da
  // questa schermata: il record condiviso non si tocca, la riga si legge come
  // non assegnata.
  const [assegnazioniAnnullate, setAssegnazioniAnnullate] = useState<
    Set<number>
  >(() => new Set());
  // Righe spuntate per la prenotazione: da qui si deriva quali checkbox
  // restano abilitati (stesso corso, stesso tipo), senza toccare il DOM.
  const { t } = useTranslation();
  const [selezione, setSelezione] = useState<SelezionePrenotazione>([]);
  const [searchDiscente, setSearchDiscente] = useState("");

  const notSubscribedErrorMessage = t("attestati-corso-non-in-abbonamento");

  const debouncedSearchDiscente = useDebounce(searchDiscente, 300);

  // Il contenuto (righe attestato/partecipanti) viene montato solo alla prima
  // apertura e poi mantenuto: evita di renderizzare i dettagli di tutti i corsi
  // in pagina, alleggerendo primo render e interazioni.
  const disclosure = Ariakit.useDisclosureStore({ defaultOpen: !!isOpen });
  const liveOpen = Ariakit.useStoreState(disclosure, "open");
  const [hasOpened, setHasOpened] = useState(!!isOpen);
  useEffect(() => {
    if (liveOpen && !hasOpened) {
      setHasOpened(true);
    }
  }, [liveOpen, hasOpened]);

  // Ricerca sul discente in pagina; le righe restano nell'ordine del backend.
  const filteredAttestati = useMemo(
    () => filtraPerTesto(attestati, debouncedSearchDiscente),
    [attestati, debouncedSearchDiscente]
  );

  // Le due handler sono stabili: la riga a cui si riferiscono arriva dal
  // figlio, così `BookableMemoized` non si ri-renderizza a ogni render del
  // padre. Leggono i prenotabili dallo stato tramite ref per non dipenderne.
  const bookableEmployeesRef = useRef(bookableEmployees);
  bookableEmployeesRef.current = bookableEmployees;

  const handleEmployeeBooked = useCallback(
    async (cf: string, { corsoMancanteId, indice }: RigaPrenotabile) => {
      if (!code) return;
      const selected = bookableEmployeesRef.current.find((e) => e.cf === cf);
      if (!selected) {
        return;
      }
      // assegno il dipendente al corso mancante
      const assegnato = await assignMissingCourse(corsoMancanteId, cf);
      if (!assegnato) {
        return;
      }
      // aggiorno la lista dei partecipanti assegnati al corso
      setSelectedEmployee((prev) => [
        ...prev,
        { cf: selected.cf, name: selected.name, index: indice },
      ]);
      // rimuovo il dipendente dalla lista dei dipendenti prenotabili
      segnaAssegnato(code, selected.cf);
    },
    [code, segnaAssegnato]
  );

  const handleUndoEmployeeBooked = useCallback(
    async (
      selected: SelectedBookableEmployeeType,
      { corsoMancanteId }: RigaPrenotabile
    ) => {
      // rimuovo il dipendente dal corso mancante
      const annullato = await assignMissingCourse(corsoMancanteId, null);
      if (!annullato) {
        return;
      }
      // rimuovo il dipendente dalla lista dei partecipanti selezionati
      setSelectedEmployee((prev) =>
        prev.filter((se) => se.index !== selected.index)
      );
      // aggiungo il dipendente alla lista dei dipendenti prenotabili, ordinando la lista
      if (code) {
        segnaAnnullato(code, { cf: selected.cf, name: selected.name });
      }
      // la riga torna a leggersi come corso mancante non assegnato
      setAssegnazioniAnnullate((prev) => new Set(prev).add(corsoMancanteId));
      // e non porta più quel discente: se era spuntata, esce dalla selezione
      setSelezione((prev) =>
        senzaRiga(
          prev,
          chiaveRiga({ id: corsoMancanteId, metadata: { mancante: true } })
        )
      );
    },
    [code, segnaAnnullato]
  );

  return (
    <Ariakit.DisclosureProvider store={disclosure}>
      <Disclosure>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "2rem",
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: Color.LIGHT_BLUE,
              fontFamily: "Avenir Next Bold, sans-serif",
              minWidth: 0,
              flex: "1 1 auto",
              paddingRight: "5rem",
            }}
          >
            <div style={{ flexShrink: 0, width: "48px", height: "48px" }}>
              {icon}
            </div>
            <CourseTitle title={title}>{title}</CourseTitle>
          </div>
          <p aria-hidden="true">
            <ChevronDown color={Color.WHITE} size={32} />
          </p>
        </div>
      </Disclosure>
      <DisclosureContent>
        {(liveOpen || hasOpened) && (
          <>
            <SearchInput style={{ width: "90%" }}>
              <div
                style={{
                  position: "relative",
                }}
              >
                <input
                  name="searchPartecipantiCorso"
                  type="text"
                  placeholder="Ricerca i partecipanti"
                  aria-label="Ricerca i partecipanti"
                  onChange={(e) => setSearchDiscente(e.target.value)}
                />
                <SearchButton>
                  <Search color={Color.WHITE} size={16} width={16} />
                </SearchButton>
              </div>
            </SearchInput>
            <div
              style={{
                display: "flex",
                width: "100%",
                flexDirection: "column",
                gap: ".5rem",
              }}
            >
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flexDirection: "column",
                    fontSize: "1.5rem",
                  }}
                >
                  <p>Caricamento dettagli...</p>
                  <BarLoader width="23ch" loading={loading} />
                </div>
              ) : filteredAttestati.length === 0 ? (
                // La scheda di un corso ha sempre almeno una riga: se non ne
                // mostra nessuna e' la ricerca sul discente a escluderle.
                <StatoVuoto
                  icona={Users}
                  titolo={t("attestati-corso-ricerca-vuoto")}
                  suggerimento={t("attestati-corso-ricerca-vuoto-suggerimento")}
                />
              ) : (
                <>
                  <TableAttestatiHeader>
                    <p>Cognome e nome</p>
                    <p>Sede aziendale</p>
                    <p>Ore di formazione</p>
                    <p>Stato</p>
                    <p>Info</p>
                    <p
                      style={{
                        justifySelf: "center",
                      }}
                    >
                      Attestato
                    </p>
                  </TableAttestatiHeader>
                  {filteredAttestati.map((a, index) => {
                    const mancante = a.metadata.mancante;
                    const cfDiscente = assegnazioniAnnullate.has(a.id)
                      ? ""
                      : a.metadata.cf_discente;
                    const mancanteAssegnato = !!(
                      mancante &&
                      cfDiscente &&
                      !a.metadata.data_prenotazione
                    );
                    const prenotato = !!(
                      mancante &&
                      cfDiscente &&
                      a.metadata.data_prenotazione
                    );
                    let selected = undefined;
                    if (mancante) {
                      if (mancanteAssegnato || prenotato) {
                        selected = {
                          cf: cfDiscente,
                          name: `${a.metadata.cognome_discente} ${a.metadata.nome_discente}`.toUpperCase(),
                          index,
                        };
                      } else {
                        selected = selectedEmployee.find(
                          (se) => se.index === index
                        );
                      }
                    }
                    const employeeCF = selected ? selected.cf : cfDiscente;
                    const showBookable =
                      (mancante || mancanteAssegnato) && !prenotato;
                    // La riga nella selezione: il discente in minuscolo, come
                    // lo ha sempre portato la prenotazione; il corso da
                    // prenotare è quello proposto dal backend per la riga
                    // (reservationCode), che dipende dal discente, altrimenti
                    // quello dell'accordion. La chiave distingue due righe
                    // della stessa persona sullo stesso corso.
                    const chiave = chiaveRiga(a);
                    const riga: RigaSelezionabile = {
                      chiave,
                      cf: (employeeCF ?? "").toLowerCase(),
                      codiceCorso: a.metadata.codice_corso,
                      codicePrenotazione: a.reservationCode || code,
                      mancante: a.metadata.mancante,
                    };
                    return (
                      <TableAttestatiRow key={`attestato_${chiave}`}>
                        {showBookable ? (
                          <BookableMemoized
                            employees={bookableEmployees}
                            selectedEmployee={selected}
                            corsoMancanteId={a.id}
                            codiceCorso={a.metadata.codice_corso}
                            indice={index}
                            onEmployeeBooked={handleEmployeeBooked}
                            undoEmployeeBooked={handleUndoEmployeeBooked}
                          />
                        ) : (
                          <p>
                            {a.metadata.cognome_discente +
                              " " +
                              a.metadata.nome_discente}
                          </p>
                        )}
                        <p>{a.metadata.sede_azienda}</p>
                        <p>{a.courseDuration} ore</p>
                        <BadgeStatoAttestato attestato={a} />
                        <InfoPrenotazione attestato={a} />
                        <ScaricaAttestato attestato={a} />
                        <div
                          style={{
                            justifySelf: "center",
                          }}
                        >
                          {a.metadata.data_prenotazione !== undefined ||
                          !a.isHired
                            ? null
                            : employeeCF &&
                              !prenotato &&
                              (a.isSubscribed ? (
                                <Suggerimento
                                  testo={
                                    selezionabile(selezione, riga)
                                      ? null
                                      : t("attestati-aiuto-riga-non-combinabile")
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    id={`attestato_checkbox_${chiave}`}
                                    aria-label={t("attestati-seleziona-riga", {
                                      discente:
                                        a.metadata.cognome_discente +
                                        " " +
                                        a.metadata.nome_discente,
                                      corso: nomeCorso(a, catalogo),
                                    })}
                                    checked={selezionata(selezione, chiave)}
                                    disabled={!selezionabile(selezione, riga)}
                                    onChange={() =>
                                      setSelezione((prev) =>
                                        toggleSelezione(prev, riga)
                                      )
                                    }
                                  />
                                </Suggerimento>
                              ) : (
                                <Ariakit.HovercardProvider>
                                  <Ariakit.HovercardAnchor render={<div />}>
                                    <TriangleAlert
                                      color={Color.RED}
                                      height={24}
                                      width={24}
                                    />
                                  </Ariakit.HovercardAnchor>
                                  <Ariakit.Hovercard
                                    fixed
                                    gutter={10}
                                    style={{
                                      backgroundColor: "var(--red500)",
                                      color: "var(--white)",
                                      padding: "1em",
                                      borderRadius: "8px",
                                      width: "28vw",
                                    }}
                                  >
                                    <p>{notSubscribedErrorMessage}</p>
                                  </Ariakit.Hovercard>
                                </Ariakit.HovercardProvider>
                              ))}
                        </div>
                      </TableAttestatiRow>
                    );
                  })}
                </>
              )}
              {/* PRENOTA resta anche quando la ricerca non mostra righe: la
                  selezione fatta prima della ricerca resta valida, e senza
                  righe spuntate il pulsante lo dice. */}
              {!loading && (
                <Suggerimento
                  testo={t("attestati-aiuto-prenota")}
                  style={{ marginLeft: "auto" }}
                >
                  <OutlineButton
                    onClick={() => {
                      // Due righe spuntate della stessa persona la
                      // prenotano una volta sola.
                      const partecipantiCorso = partecipanti(selezione);
                      if (partecipantiCorso.length > 0) {
                        // Il corso da prenotare è quello delle righe
                        // scelte: la regola di selezione lo tiene uguale
                        // per tutte.
                        const targetCode = selezione[0].codicePrenotazione;
                        vaiAllaPrenotazione(
                          dispatch,
                          partecipantiCorso,
                          targetCode
                        );
                      } else {
                        avvisoUnico(
                          "attestati-prenota-senza-partecipanti",
                          "warning",
                          "Selezionare almeno un partecipante per effettuare una prenotazione"
                        );
                      }
                    }}
                  >
                    <CalendarDays color={Color.WHITE} />
                    PRENOTA
                  </OutlineButton>
                </Suggerimento>
              )}
            </div>
          </>
        )}
      </DisclosureContent>
    </Ariakit.DisclosureProvider>
  );
}

const BookableMemoized = memo(BookableEmployees);
